import React from 'react';
import { Phone, Mail, MapPin, Facebook, Instagram, Bot, Clock, ArrowUpRight, Gift } from 'lucide-react';

interface FooterProps {
  onNavigate?: (
    page: 'impressum' | 'datenschutz' | 'buchungsbedingungen' | 'cookie-einstellungen' | 'events'
  ) => void;
}

const Footer: React.FC<FooterProps> = ({ onNavigate }) => {
  const handleLegalClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    page: 'impressum' | 'datenschutz' | 'buchungsbedingungen' | 'cookie-einstellungen' | 'events'
  ) => {
    e.preventDefault();
    if (onNavigate) onNavigate(page);
  };

  return (
    // pb-28 on mobile clears the fixed bottom nav, which otherwise covers the copyright line.
    <footer className="bg-white text-gray-900 pb-28 md:pb-0">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">

        <div className="pt-16 pb-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-10 border-b border-gray-200">

          <div className="lg:col-span-4">
            <div className="flex items-center gap-3 mb-6">
              <img src="/photos/Image.jpeg" alt="SV Heinersreuth Logo" className="h-[68px] w-[68px] object-contain rounded-full" />
              <div>
                <p className="text-[13px] font-bold text-gray-900 leading-none">SV Heinersreuth</p>
                <p className="text-[11px] text-gray-400 leading-none mt-1">Tennis & Padel</p>
              </div>
            </div>
            <p className="text-[13px] text-[color:var(--svh-ink-3)] leading-relaxed mb-6 max-w-[260px]">
              Der einzige Padel-Court in Bayreuth. Premium Tennis & Padel — mitten in Franken.
            </p>
            {/* Gift cards already live on the Events page — this only surfaces that destination. */}
            <a
              href="/events"
              onClick={(e) => handleLegalClick(e, 'events')}
              className="svh-btn svh-btn--secondary svh-btn--sm svh-focus mb-7"
            >
              <Gift size={15} className="text-[color:var(--svh-crest)]" />
              Gutschein verschenken
            </a>
            <div className="flex gap-2.5">
              <a
                href="https://www.facebook.com/svheinersreuth/?locale=de_DE"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-xl bg-gray-100 hover:bg-red-600 text-gray-500 hover:text-white flex items-center justify-center transition-all duration-200"
                aria-label="Facebook"
              >
                <Facebook size={15} />
              </a>
              <a
                href="https://www.instagram.com/sv_heinersreuth/?hl=en"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-xl bg-gray-100 hover:bg-red-600 text-gray-500 hover:text-white flex items-center justify-center transition-all duration-200"
                aria-label="Instagram"
              >
                <Instagram size={15} />
              </a>
            </div>
          </div>

          <div className="lg:col-span-3">
            <h4 className="svh-label mb-5">Kontakt</h4>
            <ul className="space-y-3.5">
              <li className="flex items-start gap-3">
                <MapPin size={14} className="text-[color:var(--svh-crest)] flex-shrink-0 mt-0.5" />
                <span className="text-[13px] text-[color:var(--svh-ink-3)] leading-relaxed">Bayreuther Str. 47<br />95500 Heinersreuth</span>
              </li>
              <li className="flex items-center gap-3">
                <Phone size={14} className="text-[color:var(--svh-crest)] flex-shrink-0" />
                <a href="tel:+4992148388" className="text-[13px] text-[color:var(--svh-ink-3)] hover:text-gray-900 transition-colors duration-200">
                  +49 921 48388
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Mail size={14} className="text-[color:var(--svh-crest)] flex-shrink-0 mt-0.5" />
                <span className="text-[13px] text-[color:var(--svh-ink-3)] break-all leading-relaxed">info@svheinersreuth-padel.de</span>
              </li>
            </ul>
          </div>

          <div className="lg:col-span-2">
            <h4 className="svh-label mb-5">Zeiten</h4>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <Clock size={14} className="text-[color:var(--svh-crest)] flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-[12px] text-[color:var(--svh-ink-3)] mb-0.5">Mo – So</p>
                  <p className="text-[13px] font-medium text-gray-700">08:00 – 23:00</p>
                </div>
              </li>
              <li className="pl-[26px] text-[12px] text-[color:var(--svh-crest)]">Flutlicht bis 23:00</li>
            </ul>
          </div>

          <div className="lg:col-span-3">
            <h4 className="svh-label mb-5">Rechtliches</h4>
            <ul className="space-y-2">
              {[
                { label: 'Datenschutz', page: 'datenschutz' as const },
                { label: 'Impressum', page: 'impressum' as const },
                { label: 'Buchungsbedingungen', page: 'buchungsbedingungen' as const },
                { label: 'Cookie-Einstellungen', page: 'cookie-einstellungen' as const },
              ].map((item) => (
                <li key={item.page}>
                  <a
                    href={`/${item.page}`}
                    onClick={(e) => handleLegalClick(e, item.page)}
                    className="group flex items-center gap-1.5 text-[13px] text-[color:var(--svh-ink-3)] hover:text-gray-900 transition-colors duration-200 py-0.5"
                  >
                    <ArrowUpRight size={11} className="opacity-0 group-hover:opacity-100 -ml-0.5 transition-opacity duration-200" />
                    {item.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="py-5 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-[12px] text-[color:var(--svh-ink-3)]">
            &copy; 2025 SV Heinersreuth 1921 e.V.
          </p>
          <a
            href="https://cogniiq.de"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 group"
          >
            <span className="text-[12px] text-[color:var(--svh-ink-3)] tracking-[0.12em] uppercase font-medium group-hover:text-gray-600 transition-colors duration-200">Powered by</span>
            <span className="flex items-center gap-1.5 bg-gray-100 border border-gray-200 group-hover:bg-gray-200 group-hover:border-gray-300 rounded-full px-3 py-1 transition-all duration-200">
              <Bot className="w-3.5 h-3.5 text-gray-500 group-hover:text-gray-700 transition-colors duration-200" />
              <span className="text-[12px] font-bold text-gray-500 group-hover:text-gray-700 tracking-[0.06em] transition-colors duration-200">Cogni<span className="text-gray-400 group-hover:text-gray-600">IQ</span></span>
            </span>
          </a>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
